/* eslint-disable @typescript-eslint/camelcase */
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import '@/DIContainer'
import { AddPolicyIndividualDTO, ClientPolicyDto, ClientPolicyService, FilterPoliciesDTO, ManualNbrDTOV2, PolicyIndividualDTO, PolicySearchDto, PolicySearchResultDto, SyncPolicyIndividualRequestDTO, SyncPolicyIndividualResponseDTO } from '../api'
import { camelCase, snakeCase } from 'lodash/fp'
import { ProductHeaders } from '@master/model/common/ProductMeta'
import { container, inject } from 'inversify-props'
import API from '@core/services/API'
import AccountMeta from '@core/pages/account/model/AccountMeta'
import { ProductRider } from '../model/ProductRider'
import { ProductILPPremium } from '../model/ProductILPPremium'
import { ProductILPSingle } from '../model/ProductILPSingle'
import { PolicyClientMeta } from '../model/PolicyClientMeta'
import { ProductFullMetaList } from '@master/model/common/ProductFullMetaList'
import { CISLumpSum, CISRepeatInvestment, PorfolioTransactions } from '../model/CISPortfolio'
import { PolicyRoles } from '@master/model/options/PolicyRoles'
import { SharedVariable } from '@core/model/SharedVariable'
import { LineOfBusinessOptions } from '@master/model/options/LineOfBusinessOptions'
import { NBRStatusOptions } from '@master/model/options/NBRStatusOptions'
import { NBRProductStatusOptions, NBRProductCISStatusOptions } from '@master/model/options/NBRProductStatusOptions'
import { TypeOfServiceOptions } from '@master/model/options/TypeOfServiceOptions'
import { PaymentMethodOptions } from '@master/model/options/PaymentMethodOptions'
import { CurrencyOptions } from '@master/model/options/CurrencyOptions'
import { FrequencyOptions } from '@master/model/options/FrequencyOptions'
import { PaymentModeOptions } from '@master/model/options/PaymentModeOptions'
import { RelationshipOptions } from '@master/model/options/RelationshipOptions'
import { IdentityTypeOptions } from '@master/model/options/IdentityTypeOptions'
import { CitizenshipOptions } from '@master/model/options/CitizenshipOptions'
import { MaritalStatusOptions } from '@master/model/options/MaritalStatusOptions'
import { EduLevelOptions, SpokenLangOptions, WrittenLangOptions, IncomeTierOptions } from '@core/pages/client-management/model/ClientPersonal'
import { TransactionTypeOptions } from '@master/model/options/TransactionTypeOptions'
import { WrapOptions } from '@master/model/options/WrapOptions'
import { ProductList } from '@core/pages/adminProductList/model/ProductList'

const $api = container.get<API>('$api')

@Module({
  namespaced: true
})
export default class ClientPolicyStore extends VuexModule {
  @inject()
  private sharedVariable: SharedVariable

  loading = false
  policy: ClientPolicyDto = {
    pky: undefined,
    oid: undefined,
    ctm: undefined,
    clientOid: undefined,
    adviserOid: undefined,
    introducerOid: undefined,
    introducerPct: undefined,
    introducerIfam: undefined,
    servicingOid: undefined,
    servicingPct: undefined,
    servicingIfam: undefined,
    vestingOid: undefined,
    vestingPct: undefined,
    vestingIfam: undefined,
    ownersOid: undefined,
    ownersName: undefined,
    ownersNric: undefined,
    insuredTitle: undefined,
    insuredName: undefined,
    insuredNric: undefined,
    insuredTitleTwo: undefined,
    insuredNameTwo: undefined,
    insuredNricTwo: undefined,
    productOid: undefined,
    name: undefined,
    productTypeDetailed: undefined,
    productCategory: undefined,
    taggedKyc: undefined,
    type: undefined,
    policyNumber: undefined,
    businessId: undefined,
    principal: undefined,
    status: undefined,
    applicationDate: undefined,
    opsApprovalDate: undefined,
    approvedDate: undefined,
    startDate: undefined,
    endDate: undefined,
    bscClawbackStatus: undefined,
    bscClawbackAmount: undefined,
    clientRebateStatus: undefined,
    clientRebateAmount: undefined,
    clientAiStatus: undefined,
    productMetadata: undefined,
    clientMetadata: undefined,
    creationType: undefined,
    faceToFace: undefined,
    faceToFaceSpecified: undefined,
    policyProfileRemarks: undefined,
    level1ComOid: undefined,
    level2ComOid: undefined,
    level3ComOid: undefined,
    level4ComOid: undefined,
    level1ComPct: undefined,
    level2ComPct: undefined,
    level3ComPct: undefined,
    level4ComPct: undefined,
    groupId: undefined,
    unitName: undefined,
    branchName: undefined,
    clusterName: undefined,
    mainInvestmentNbr: undefined,
    mainNbrOid: undefined
  }

  // NBR Version 2 Requirements (START)

  policyIndividuals: PolicyIndividualDTO[] = []

  subPolicies: ClientPolicyDto[] = []

  policyRoles = PolicyRoles

  policyInView: ClientPolicyDto = null

  mainPolicies: PolicySearchDto[] = []

  syncClientResponse: SyncPolicyIndividualResponseDTO = null

  // NBR Version 2 Requirements (END)

  clientMetadata = new PolicyClientMeta()

  productMetadata: any = null

  productMetaList: ProductFullMetaList = new ProductFullMetaList([])

  adviserDetails = new AccountMeta()

  commissionUserDetails: any = {}

  // get all reusable options
  lineOfBusinessOptions = LineOfBusinessOptions
  nbrStatusOptions = NBRStatusOptions.filter(option => option.value !== 'new')
  nbrProductStatusOptions = NBRProductStatusOptions
  nbrProductCisStatusOptions = NBRProductCISStatusOptions
  typeOfServiceOptions = TypeOfServiceOptions
  paymentMethodOptions = PaymentMethodOptions
  currencyOptions = CurrencyOptions
  frequencyOptions = FrequencyOptions
  paymentModeOptions = PaymentModeOptions
  relationshipOptions = RelationshipOptions
  identityTypeOptions = IdentityTypeOptions
  citizenshipOptions = CitizenshipOptions
  maritalStatusOptions = MaritalStatusOptions
  eduLevelOptions = EduLevelOptions
  spokenLangOptions = SpokenLangOptions
  writtenLangOptions = WrittenLangOptions
  incomeTierOptions = IncomeTierOptions
  transactionTypeOptions = TransactionTypeOptions
  wrapOptions = WrapOptions

  get venueOptions () {
    return this.sharedVariable?.options?.venueOptions || []
  }

  get convertedKeyClientPolicy () {
    const convertedKeys: Record<string, any> = {}
    for (const key in this.policy) {
      convertedKeys[snakeCase(key)] = this.policy[key]
    }
    return convertedKeys
  }

  @Action
  async listPolicies (filter: FilterPoliciesDTO) {
    this.context.commit('setLoading', true)

    try {
      const policies = await ClientPolicyService.listPoliciesV2(filter)
      this.context.commit('setMainPolicies', policies)
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Mutation
  setMainPolicies (policies: PolicySearchResultDto) {
    if (policies.data.length > 0) {
      this.mainPolicies = policies.data.map(policy => {
        if (typeof policy.productMetadata === 'string') {
          policy.productMetadata = JSON.parse(policy.productMetadata)
        }
        if (typeof policy.role === 'string') {
          policy.role = JSON.parse(policy.role)
        }
        return policy
      })
    }
  }

  @Action
  async updateFromIframe (obj: Record<string, any>) {
    const convertedKeys: Record<string, any> = {}
    for (const key in obj) {
      convertedKeys[camelCase(key)] = obj[key]
    }
    this.context.commit('updatePolicy', convertedKeys)
  }

  @Action({ rawError: true })
  async savePolicy () {
    this.context.commit('setLoading', true)

    try {
      const selectedProduct = this.productMetaList?.getFullList().find(prod => prod.oid === this.policy.productOid)

      if (selectedProduct) {
        this.policy.name = selectedProduct.name
        this.policy.productCategory = selectedProduct.category
        this.policy.productTypeDetailed = selectedProduct.type
      }
      this.policy.clientMetadata = JSON.stringify(this.clientMetadata.convertToRawData())
      this.policy.productMetadata = JSON.stringify(this.productMetadata)
      await ClientPolicyService.updatePolicy(this.policy)
    } catch (error) {
      alert(error.message)
    }

    this.context.commit('setLoading', false)
  }

  @Action
  async fetchPolicy (oid: string) {
    this.context.commit('setLoading', true)
    try {
      const res = await ClientPolicyService.getPolicy(oid) as any

      if (typeof res === 'string' && JSON.parse(res).error) {
        throw new Error(JSON.parse(res).error)
      }

      this.context.commit('setPolicy', res)
    } catch (error) {
      alert(error.message)
    }
    this.context.commit('setLoading', false)
  }

  @Action
  async fetchPolicyV2 (oid: string) {
    this.context.commit('setLoading', true)
    try {
      const res = await ClientPolicyService.getPolicyV2(oid) as any

      if (typeof res === 'string' && JSON.parse(res).error) {
        throw new Error(JSON.parse(res).error)
      }

      this.context.commit('setPolicy', res)
    } catch (error) {
      alert(error.message)
    }
    this.context.commit('setLoading', false)
  }

  // NBR Version 2 Requirements (START)

  @Action
  async retrieveSubPolicies (mainPolicyOid: string) {
    this.context.commit('setLoading', true)
    try {
      const res = await ClientPolicyService.getSubPolicy(mainPolicyOid)
      if (typeof res === 'string' && JSON.parse(res).error) {
        throw new Error(JSON.parse(res).error)
      }
      this.context.commit('setSubPolicies', res)
    } catch (error) {
      alert(error.message)
    }
    this.context.commit('setLoading', false)
  }

  @Mutation
  setSubPolicies (data: ClientPolicyDto[]) {
    if (data && data.length) {
      this.subPolicies = data.map(policy => {
        return {
          ...policy,
          productMetadata: JSON.parse(policy.productMetadata) || {}
        }
      })
    }
  }

  @Action
  async saveSubPolicy (subPolicy: ClientPolicyDto) {
    this.context.commit('setLoading', true)
    try {
      subPolicy.productMetadata = JSON.stringify(subPolicy.productMetadata)
      const productList: ProductList = this.context.rootState.KYCFormStore.productList
      const selectedProduct = productList.originalList.find(prod => prod.oid === subPolicy.productOid)

      if (selectedProduct) {
        subPolicy.name = selectedProduct.name
        subPolicy.productCategory = selectedProduct.category
        subPolicy.productTypeDetailed = selectedProduct.type
      }
      const createdPolicyOid = await ClientPolicyService.updatePolicy(subPolicy)
      // Doing so because version 1 API returns it as a string...
      subPolicy.productMetadata = JSON.parse(subPolicy.productMetadata)
      this.context.commit('updatePolicyInView', subPolicy)
      return createdPolicyOid
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Action
  async addNewSubPolicy (subPolicy: ManualNbrDTOV2) {
    this.context.commit('setLoading', true)

    try {
      const createdPolicy = await ClientPolicyService.saveNewBusinessV2(subPolicy)
      this.context.commit('updatePolicyInView', createdPolicy)
      this.context.commit('addSubPolicyToArray', createdPolicy)
      return createdPolicy
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Action
  selectedPolicyInView (policy: ClientPolicyDto) {
    this.context.commit('updatePolicyInView', policy)
  }

  @Mutation
  updatePolicyInView (policy: ClientPolicyDto) {
    if (typeof policy?.productMetadata === 'string') {
      policy.productMetadata = JSON.parse(policy.productMetadata)
    }
    this.policyInView = policy
  }

  @Mutation
  addSubPolicyToArray (policy: ClientPolicyDto) {
    this.subPolicies.push(policy)
  }

  @Action
  async removeSubPolicy (oid: string) {
    const subPolicy = this.subPolicies.find(policy => policy.oid === oid)

    if (!subPolicy) {
      return
    }

    // Delete from database
    this.context.commit('setLoading', true)

    try {
      const deletedPolicy = await ClientPolicyService.deleteSubPolicy(subPolicy.oid)
      this.context.commit('removeSubPolicyFromArray', deletedPolicy)
      return deletedPolicy
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Mutation
  removeSubPolicyFromArray (policy: ClientPolicyDto) {
    const index = this.subPolicies.findIndex(subPolicy => subPolicy.oid === policy.oid)

    if (index !== -1) {
      this.subPolicies.splice(index, 1)
      this.policyInView = null
    }
  }

  @Action
  async addPolicyIndividual (individual: AddPolicyIndividualDTO) {
    this.context.commit('setLoading', true)
    try {
      const res = await ClientPolicyService.addNewPolicyIndividuals(individual) as any

      if (typeof res === 'string' && JSON.parse(res).error) {
        throw new Error(JSON.parse(res).error)
      }

      this.context.commit('setSubPolicies', res)
      return res
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Action
  async savePolicyIndividual (individual: PolicyIndividualDTO) {
    this.context.commit('setLoading', true)
    try {
      const updatedIndividual = await ClientPolicyService.savePolicyIndividual(individual)
      return updatedIndividual
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Action
  async deletePolicyIndividual (individual: PolicyIndividualDTO) {
    this.context.commit('setLoading', true)
    try {
      const deletedIndividual = await ClientPolicyService.deletePolicyIndividual(individual.oid)
      return deletedIndividual
    } catch (error) {
      alert(error.message)
    } finally {
      this.context.commit('setLoading', false)
    }
  }

  @Action
  async fetchPolicyIndividuals (oid: string) {
    this.context.commit('setLoading', true)
    try {
      const res = await ClientPolicyService.retrievePolicyIndividuals(oid) as any

      if (typeof res === 'string' && JSON.parse(res).error) {
        throw new Error(JSON.parse(res).error)
      }

      this.context.commit('setPolicyIndividual', res)
    } catch (error) {
      alert(error.message)
    }
    this.context.commit('setLoading', false)
  }

  @Mutation
  setPolicyIndividual (data: PolicyIndividualDTO[]) {
    if (data && data.length) {
      this.policyIndividuals = data.sort((a, b) => Number(b.isMainOwner) - Number(a.isMainOwner))
    }
  }

  // NBR Version 2 Requirements (END)

  @Mutation
  setProductMetaList (data: (string | number)[][]) {
    if (data && data.length) {
      const productMetaList = new ProductFullMetaList(data)
      this.productMetaList = productMetaList
    }
  }

  @Mutation
  setAdviserDetails (data: (string | number)[]) {
    if (data) {
      this.adviserDetails.assignData(Object.keys(data), Object.values(data))
    }
  }

  @Action({ rawError: true })
  async fetchRecommendedProducts () {
    const { data } = await $api.relProductList.list({
      headers: JSON.stringify(ProductHeaders),
      // only return active products
      query: 'status = ?',
      queryArgs: JSON.stringify([1])
    })
    this.context.commit('setProductMetaList', data)
  }

  @Action({ rawError: true })
  async fetchAdviserDetails () {
    const { meta } = await $api.account.loginMetaGet({ _oid: this.policy.adviserOid })
    this.context.commit('setAdviserDetails', meta)
  }

  @Mutation
  setCommissionUserDetails (data: {}) {
    if (data) {
      this.commissionUserDetails = data
    }
  }

  @Action({ rawError: true })
  async fetchCommissionUserDetails () {
    const lvl1ComUser = await $api.account.loginMetaGet({ _oid: this.policy.level1ComOid })
    const lvl2ComUser = await $api.account.loginMetaGet({ _oid: this.policy.level2ComOid })
    const lvl3ComUser = await $api.account.loginMetaGet({ _oid: this.policy.level3ComOid })
    const data = {
      lvl1ComUser: lvl1ComUser.meta,
      lvl2ComUser: lvl2ComUser.meta,
      lvl3ComUser: lvl3ComUser.meta
    }
    this.context.commit('setCommissionUserDetails', data)
  }

  @Action({ rawError: true })
  async syncClientInformation (syncDto: SyncPolicyIndividualRequestDTO) {
    const response = await ClientPolicyService.syncClientInformation(syncDto)
    this.context.commit('setSyncClientResponse', response)
  }

  @Mutation
  setSyncClientResponse (syncResponse: SyncPolicyIndividualResponseDTO) {
    this.syncClientResponse = syncResponse
  }

  @Mutation
  setPolicy (policy: ClientPolicyDto) {
    this.policy = policy
    let productMetadata = JSON.parse(policy.productMetadata)
    const parsedClientMetadata = JSON.parse(policy.clientMetadata) || {}
    this.clientMetadata.assignData(Object.keys(parsedClientMetadata), Object.values(parsedClientMetadata))

    if (typeof this.clientMetadata.pdpaMarketing === 'boolean') {
      this.clientMetadata.pdpaMarketing = []
    }

    if (!productMetadata) {
      productMetadata = {}
    }

    productMetadata.insured_dob = productMetadata.insured_dob || ''
    productMetadata.insured_citizenship = productMetadata.insured_citizenship || ''
    productMetadata.nationality = productMetadata.nationality || ''
    productMetadata.gender = productMetadata.gender || ''
    productMetadata.relationship_to_owner = productMetadata.relationship_to_owner || ''

    if (this.policy.type === 'collectiveinvestmentscheme') {
      if (!productMetadata.cis_lump_sum) {
        productMetadata.cis_lump_sum = [new CISLumpSum()]
      }

      if (!productMetadata.cis_repeat_investment) {
        productMetadata.cis_repeat_investment = [new CISRepeatInvestment()]
      }
      if (!productMetadata.portfolio_transactions) {
        productMetadata.portfolio_transactions = [new PorfolioTransactions()]
      }
    }

    if (this.policy.type === 'lifeinsurance') {
      if (!productMetadata.riders) {
        productMetadata.riders = [new ProductRider()]
      }

      if (!productMetadata.ilp_premium_list) {
        productMetadata.ilp_premium_list = [new ProductILPPremium()]
      }

      if (!productMetadata.ilp_single_list) {
        productMetadata.ilp_single_list = [new ProductILPSingle()]
      }
    }

    this.productMetadata = {
      frequency: '',
      basicModalPremium: 0,
      basic_modal_premium: 0,
      ncBasicModalPremium: 0,
      nc_basic_modal_premium: 0,
      modalPremium: 0,
      modal_premium: 0,
      asp: 0,
      discount: 0,
      gst: 0,
      premiumPayable: 0,
      premium_payable: 0,
      netPremium: 0,
      net_premium: 0,
      ape: 0,
      commissionPayable: 0,
      grossCommissionEarned: 0,
      commissionPayment: 0,
      ...productMetadata
    }

    // Assign product issuer and name, if product name is null, but have product oid
    if (this.policy.productOid && !this.policy?.name) {
      const product = this.productMetaList.allProduct.find(product => product.oid === this.policy.productOid)
      const name = product?.name
      const issuer = product?.issuer_oid
      this.policy.name = name

      if (!this.policy.principal) {
        this.policy.principal = issuer
      }
    }

    // Set default policy.status as 'pending', instead of 'new'
    if (this.policy.status === 'new') this.policy.status = 'pending'
  }

  @Mutation
  updatePolicy (update: Record<keyof ClientPolicyDto, any>) {
    Object.assign(this.policy, update)
  }

  @Mutation
  setLoading (loading: boolean) {
    this.loading = loading
  }
}
